import { prisma } from "@/lib/db";
import {
  STABLE_CORRECT_COUNT,
  STABLE_SESSION_COUNT,
  BOX_GRADUATED,
} from "./constants";

/**
 * Normalize answer for comparison
 */
export function normalizeAnswer(input: string): string {
  return input.trim().toLowerCase();
}

/**
 * Check if answer is correct
 */
export function checkAnswer(userInput: string, correctAnswer: string): boolean {
  return normalizeAnswer(userInput) === normalizeAnswer(correctAnswer);
}

/**
 * Count unique sessions from comma-separated session IDs
 */
function countUniqueSessions(sessionsCorrect: string): number {
  if (!sessionsCorrect) return 0;
  const sessions = sessionsCorrect.split(",").filter(s => s.trim());
  return new Set(sessions).size;
}

/**
 * Add session to sessions correct string
 */
function addSessionToList(sessionsCorrect: string, sessionId: string): string {
  if (!sessionsCorrect) return sessionId;
  const sessions = sessionsCorrect.split(",").filter(s => s.trim());
  if (!sessions.includes(sessionId)) {
    sessions.push(sessionId);
  }
  return sessions.join(",");
}

/**
 * Calculate new box level after correct answer (Phase 1)
 *
 * Box promotion logic:
 * - Box 0 (first-try correct): Jump to Box 3 (mastered!) - you already know it
 * - Box 0 (not first try): Normal progression through boxes
 * - Box 1 -> 2: Got 2 correct total
 * - Box 2 -> 3: Got 3 correct across 2+ sessions
 * - Box 3: Stay (ready for graduation)
 *
 * Key insight: If you get it right on the first try, you already know it.
 * Only cards you got wrong need the repetition cycle.
 */
function calculateNewBoxOnCorrect(
  currentBox: number,
  newCorrectCount: number,
  uniqueSessionCount: number,
  isFirstEverAttempt: boolean = false
): number {
  // Graduated cards stay graduated
  if (currentBox === BOX_GRADUATED) {
    return BOX_GRADUATED;
  }

  // Box 0 with first-try correct -> Box 3 (mastered!)
  // If it's the first ever attempt AND they got it right, they already know it
  if (currentBox === 0 && isFirstEverAttempt && newCorrectCount === 1) {
    return 3;
  }

  // Box 0 -> 1: Regular progression (got correct after previously missing)
  if (currentBox === 0 && newCorrectCount >= 1) {
    return 1;
  }

  // Box 1 -> 2: Second correct
  if (currentBox === 1 && newCorrectCount >= 2) {
    return 2;
  }

  // Box 2 -> 3: Third correct AND across 2+ sessions
  if (currentBox === 2 && newCorrectCount >= STABLE_CORRECT_COUNT && uniqueSessionCount >= STABLE_SESSION_COUNT) {
    return 3;
  }

  // Box 3: Stay stable
  return currentBox;
}

/**
 * Calculate new box level after wrong answer (Phase 1)
 *
 * On miss from Box 0: Move to Box 1 (enters learning cycle)
 * On miss from Box 1+: Drop back one box (minimum 1)
 *
 * Note: Box 0 is the "triage" box - cards either graduate to Box 3
 * (first-try correct) or enter the learning cycle at Box 1 (wrong).
 * Once in the learning cycle, cards never go back to Box 0.
 */
function calculateNewBoxOnWrong(currentBox: number): number {
  // Graduated cards drop to box 2 on wrong
  if (currentBox === BOX_GRADUATED) {
    return 2;
  }

  // Box 0 wrong -> Box 1 (enters learning cycle)
  if (currentBox === 0) {
    return 1;
  }

  // Box 1+ wrong -> drop one box (minimum 1, never back to Box 0)
  return Math.max(1, currentBox - 1);
}

export interface BoxUpdateResult {
  oldBox: number;
  newBox: number;
  correctCount: number;
  sessionsCorrect: string;
  promoted: boolean;
  demoted: boolean;
}

/**
 * Update problem state after a review (Phase 1 box system)
 */
export async function updateStateAfterAnswer(
  userId: string,
  problemId: string,
  sessionId: string,
  wasCorrect: boolean
): Promise<BoxUpdateResult> {
  const now = new Date();

  // Get current state
  const currentState = await prisma.userProblemState.findUnique({
    where: {
      userId_problemId: { userId, problemId },
    },
  });

  const oldBox = currentState?.box ?? 0;
  const oldCorrectCount = currentState?.correctCount ?? 0;
  const oldSessionsCorrect = currentState?.sessionsCorrect ?? "";

  // Check if this is the first ever attempt for this problem
  // (no state exists, or state exists but lastAnsweredAt is null)
  const isFirstEverAttempt = !currentState || !currentState.lastAnsweredAt;

  let newBox: number;
  let newCorrectCount: number;
  let newSessionsCorrect: string;

  if (wasCorrect) {
    // Increment correct count and add session
    newCorrectCount = oldCorrectCount + 1;
    newSessionsCorrect = addSessionToList(oldSessionsCorrect, sessionId);
    const uniqueSessions = countUniqueSessions(newSessionsCorrect);

    // Calculate new box (pass isFirstEverAttempt for Box 0 first-try logic)
    newBox = calculateNewBoxOnCorrect(oldBox, newCorrectCount, uniqueSessions, isFirstEverAttempt);
  } else {
    // On wrong: move to learning cycle and reset correct count
    newBox = calculateNewBoxOnWrong(oldBox);
    newCorrectCount = 0;
    newSessionsCorrect = "";  // Reset session tracking
  }

  // Upsert the state
  await prisma.userProblemState.upsert({
    where: {
      userId_problemId: { userId, problemId },
    },
    update: {
      box: newBox,
      correctCount: newCorrectCount,
      sessionsCorrect: newSessionsCorrect,
      lastAnsweredAt: now,
      lastResult: wasCorrect ? "correct" : "wrong",
      // Keep legacy set field in sync for now
      set: newBox === BOX_GRADUATED ? "old" : "focus",
    },
    create: {
      userId,
      problemId,
      box: newBox,
      correctCount: newCorrectCount,
      sessionsCorrect: newSessionsCorrect,
      lastAnsweredAt: now,
      lastResult: wasCorrect ? "correct" : "wrong",
      set: "focus",
    },
  });

  return {
    oldBox,
    newBox,
    correctCount: newCorrectCount,
    sessionsCorrect: newSessionsCorrect,
    promoted: newBox > oldBox,
    demoted: newBox < oldBox,
  };
}

/**
 * Update problem state for Phase 2 (Leitner intervals)
 */
export async function updateStatePhase2(
  userId: string,
  problemId: string,
  wasCorrect: boolean,
  intervalDays: number
): Promise<void> {
  const now = new Date();

  // Get current state
  const currentState = await prisma.userProblemState.findUnique({
    where: {
      userId_problemId: { userId, problemId },
    },
  });

  const currentBox = currentState?.box ?? 1;
  let newBox: number;
  let dueAt: Date;

  if (wasCorrect) {
    // Promote to next box (max 5)
    newBox = Math.min(5, currentBox + 1);
  } else {
    // Demote to box 1
    newBox = 1;
  }

  // Calculate next due date
  dueAt = new Date(now.getTime() + intervalDays * 24 * 60 * 60 * 1000);

  await prisma.userProblemState.update({
    where: {
      userId_problemId: { userId, problemId },
    },
    data: {
      box: newBox,
      dueAt,
      lastAnsweredAt: now,
      lastResult: wasCorrect ? "correct" : "wrong",
    },
  });
}
